import type { PermissionName } from "./catalog";
import { hasAnyPermissionInList, hasPermissionInList } from "./checks";
import { moduleAnyOf, type PermissionModulePrefix } from "./modules";
import type { PermissionAction, UserPermission } from "./types";

export function hasModuleAccess(
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
): boolean {
  return hasAnyPermissionInList(userPermissions, moduleAnyOf(prefix));
}

export function getModulePermission(
  prefix: PermissionModulePrefix,
  action: PermissionAction,
): PermissionName {
  for (const name of moduleAnyOf(prefix)) {
    const actionPart = name.slice(name.indexOf(".") + 1).trim();
    if (actionPart === action) {
      return name;
    }
  }

  return `${prefix}.${action}` as PermissionName;
}

function canPerformModuleAction(
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
  action: PermissionAction,
): boolean {
  return hasPermissionInList(
    userPermissions,
    getModulePermission(prefix, action),
  );
}

export const canViewModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "view");

export const canCreateModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "create");

export const canUpdateModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "update");

export const canDeleteModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "delete");

export const canApproveModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "approve");

export const canCancelModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "cancel");

export const canRejectModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "reject");

export const canReceiveModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "receive");

export const canConfirmModule = (
  userPermissions: readonly UserPermission[],
  prefix: PermissionModulePrefix,
) => canPerformModuleAction(userPermissions, prefix, "confirm");

/** Show Inventory in sidebar — user has any inventory or opening balance permission */
export function canAccessInventoryNav(
  userPermissions: readonly UserPermission[],
): boolean {
  return (
    hasModuleAccess(userPermissions, "inventory") ||
    hasModuleAccess(userPermissions, "openingBalance")
  );
}

export function getInventoryRoutePermissions(): PermissionName[] {
  return [...moduleAnyOf("inventory"), ...moduleAnyOf("openingBalance")];
}
